/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useTranslation } from "react-i18next";
import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { useApiClient } from "common/ApiClientProvider";

function Authentication() {
  const { t } = useTranslation("pages");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleConfirmOpen = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const apiClient = useApiClient();
  async function resetMFA() {
    handleLoadingOpen();
    try {
      await apiClient.post("reset_mfa");
    } finally {
      handleLoadingClose();
    }
  }
  return (
    <Card id="2fa" sx={{ overflow: "visible" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDTypography variant="h5">{t("accountSettings.twoFactorAuthentication")}</MDTypography>
        <MDBadge
          variant="contained"
          color="success"
          badgeContent={t("accountUi.enabled")}
          container
        />
      </MDBox>
      <MDBox p={3}>
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loadingOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Modal
          open={confirmOpen}
          onClick={handleConfirmClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox
            p={5}
            position="absolute"
            color="white"
            bgColor="white"
            transform="translate(-50%, -50%)"
          >
            <MDTypography variant="h5">
              {t("accountSettings.changeTwoFactorConfirmation")}
            </MDTypography>
            <br />
            <Stack spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  resetMFA();
                  handleConfirmClose();
                }}
              >
                {t("accountSettings.changeTwoFactorAction")}
              </MDButton>
              <MDButton p={5} variant="gradient" color="dark" onClick={handleConfirmClose}>
                {t("accountSettings.authenticationOptionKeep")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <MDTypography variant="body2" color="text" fontWeight="bold">
          {t("accountSettings.transitionProcess")}
        </MDTypography>
        <MDBox component="ol" m={0} mt={0.5} pl={3}>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            {t("accountSettings.authenticationStep1")}
          </MDTypography>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            {t("accountSettings.authenticationStep2")}
          </MDTypography>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            {t("accountSettings.authenticationStep3")}
            <MDBox component="ul" m={0} mt={0.25} pl={3}>
              <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
                {t("accountSettings.changeTwoFactorAction")}
              </MDTypography>
              <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
                {t("accountSettings.authenticationOptionKeep")}
              </MDTypography>
            </MDBox>
          </MDTypography>
        </MDBox>
        <MDBox mt={2}>
          <MDButton variant="gradient" color="error" size="small" onClick={handleConfirmOpen}>
            {t("accountSettings.clear")}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default Authentication;
